import { useCallback, useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  Card,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import {
  Add as AddIcon,
  Home as HomeIcon,
  NavigateNext as NavigateNextIcon,
} from '@mui/icons-material'

import { DashboardLayout } from '@/components/layout/DashboardLayout'
import { PageHelpPopover } from '@/components/common/PageHelpPopover'
import { adminProgramsApi, type Program } from '@/api/programs'
import { ROUTES } from '@/utils/constants'
import { getErrorMessage } from '@/utils/error'

/**
 * Системные программы платформы — готовый контент EasyA.
 *
 * Здесь только каталог и создание: содержимое правится обычным редактором
 * программ, куда админа пускает Policy. Держать вторую копию редактора значило
 * бы однажды получить два разных конструктора одной сущности.
 */
export function AdminProgramsPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const [programs, setPrograms] = useState<Program[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(0)
  const [perPage, setPerPage] = useState(20)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [createOpen, setCreateOpen] = useState(false)
  const [form, setForm] = useState({ name: '', level: '', slug: '', description: '' })
  const [saving, setSaving] = useState(false)
  const [dialogError, setDialogError] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      return await adminProgramsApi.list({ page: page + 1, per_page: perPage })
    } catch (err) {
      setError(getErrorMessage(err) || t('common.error'))

      return null
    } finally {
      setLoading(false)
    }
  }, [page, perPage, t])

  useEffect(() => {
    // Флаг отмены: ответ старого запроса не должен перетереть свежий список
    let cancelled = false

    void load().then((result) => {
      if (cancelled || result === null) return
      setPrograms(result.data)
      setTotal(result.meta?.total ?? result.data.length)
    })

    return () => {
      cancelled = true
    }
  }, [load])

  // Страница за пределами выдачи: программы с неё удалили из другой сессии или
  // выдача просто стала короче. MUI на таком page ругается и рисует пустой
  // диапазон, поэтому возвращаемся на последнюю существующую страницу сами
  useEffect(() => {
    if (loading || page === 0 || page * perPage < total) return

    setPage(total === 0 ? 0 : Math.ceil(total / perPage) - 1)
  }, [loading, page, perPage, total])

  const closeCreate = () => {
    setCreateOpen(false)
    setForm({ name: '', level: '', slug: '', description: '' })
    setDialogError(null)
  }

  const create = async () => {
    setSaving(true)
    setDialogError(null)
    try {
      const created = await adminProgramsApi.create({
        name: form.name,
        level: form.level || null,
        slug: form.slug || null,
        description: form.description || null,
      })
      closeCreate()
      navigate(ROUTES.PROGRAM_VIEW(created.id))
    } catch (err) {
      setDialogError(getErrorMessage(err) || t('common.error'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <DashboardLayout>
      <Box sx={{ p: 3 }}>
        <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} sx={{ mb: 2 }}>
          <Link to={ROUTES.DASHBOARD} style={{ display: 'flex', alignItems: 'center' }}>
            <HomeIcon fontSize="small" />
          </Link>
          <Typography color="text.primary">{t('admin.programs.title')}</Typography>
        </Breadcrumbs>

        <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h4" component="h1" fontWeight={700}>
              {t('admin.programs.title')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('admin.programs.subtitle')}
            </Typography>
          </Box>
          <PageHelpPopover pageKey="admin-programs" />
        </Box>

        <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
          <Button
            variant="contained"
            size="small"
            sx={{ height: 40 }}
            startIcon={<AddIcon />}
            onClick={() => setCreateOpen(true)}
          >
            {t('admin.programs.create')}
          </Button>
        </Stack>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading && <Skeleton variant="rounded" height={280} />}

        {!loading && programs.length === 0 && (
          <Card sx={{ p: 3 }}>
            {/* Опустевшая страница — не пустой каталог: программы есть, но на
                предыдущих страницах, и говорить админу «системных программ пока
                нет» здесь было бы неправдой */}
            <Typography color="text.secondary">
              {page > 0
                ? t('admin.programs.emptyPage', 'На этой странице программ не осталось')
                : t('admin.programs.empty')}
            </Typography>
          </Card>
        )}

        {!loading && programs.length > 0 && (
          <TableContainer component={Card}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t('admin.programs.name')}</TableCell>
                  <TableCell>{t('admin.programs.level')}</TableCell>
                  <TableCell>{t('admin.programs.themes')}</TableCell>
                  <TableCell>{t('admin.programs.slug')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {programs.map((program) => (
                  <TableRow
                    key={program.id}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => navigate(ROUTES.PROGRAM_VIEW(program.id))}
                  >
                    <TableCell>{program.name}</TableCell>
                    <TableCell>{program.level ?? '—'}</TableCell>
                    <TableCell>{program.themes_count ?? 0}</TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {program.slug ?? '—'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Пагинация вне ветки с данными: перелистывание уводит список в
            загрузку, и внутри ветки контрол исчезал бы вместе с ним — прыгая
            из-под курсора. А со страницы, которая опустела, вернуться назад
            было нечем */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            mt: 2,
            overflow: 'hidden',
            minWidth: 0,
          }}
        >
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={perPage}
            onRowsPerPageChange={(event) => {
              setPerPage(parseInt(event.target.value, 10))
              setPage(0)
            }}
            rowsPerPageOptions={[10, 20, 50]}
            labelRowsPerPage={t('admin.programs.rowsPerPage')}
            labelDisplayedRows={({ from, to, count }) => `${from}–${to} ${t('common.of')} ${count}`}
          />
        </Box>

        <Dialog open={createOpen} onClose={closeCreate} fullWidth maxWidth="sm">
          <DialogTitle>{t('admin.programs.create')}</DialogTitle>
          <DialogContent dividers>
            <Stack spacing={2} sx={{ pt: 1 }}>
              {dialogError && <Alert severity="error">{dialogError}</Alert>}
              <TextField
                fullWidth
                size="small"
                label={t('admin.programs.name')}
                value={form.name}
                onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))}
              />
              <TextField
                fullWidth
                size="small"
                label={t('admin.programs.level')}
                value={form.level}
                onChange={(event) => setForm((current) => ({ ...current, level: event.target.value }))}
              />
              <TextField
                fullWidth
                size="small"
                label={t('admin.programs.slug')}
                helperText={t('admin.programs.slugHint')}
                value={form.slug}
                onChange={(event) => setForm((current) => ({ ...current, slug: event.target.value }))}
              />
              <TextField
                fullWidth
                multiline
                minRows={3}
                size="small"
                label={t('admin.programs.description')}
                value={form.description}
                onChange={(event) => setForm((current) => ({ ...current, description: event.target.value }))}
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={closeCreate}>{t('common.cancel')}</Button>
            <Button variant="contained" disabled={saving || form.name.trim() === ''} onClick={() => void create()}>
              {saving ? t('common.loading') : t('common.create')}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </DashboardLayout>
  )
}
